import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gtmNewsletterSubmit } from '../utils/gtm';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  TextField,
} from '@mui/material';
import {
  CalculateOutlined as CalculateIcon,
  PieChartOutlined as PieChartIcon,
  CreditCardOutlined as CreditCardIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  AccessTime,
  CreditScore as CreditScoreIcon,
  Receipt as ReceiptIcon,
  House as HouseIcon,
  Savings as SavingsIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline,
} from '@mui/icons-material';
import postsData from '../data/postsData';
import SEOHead from '../components/SEO/SEOHead';
import BreadcrumbNav from '../components/SEO/BreadcrumbNav';
import TrustStrip from '../components/Layout/TrustStrip';

/* ── Helpers ─────────────────────────────────────────────────────────── */

const postById = (id) => postsData.find((p) => p.id === id);

/* ── Data constants ──────────────────────────────────────────────────── */

const LEVEL_CONFIG = {
  basico: { label: 'Básico', color: '#2E7D32', bg: '#E8F5E9' },
  intermedio: { label: 'Intermedio', color: '#7D5A00', bg: '#FFF8E1' },
  avanzado: { label: 'Avanzado', color: '#C62828', bg: '#FFEBEE' },
};

const ESSENTIAL_GUIDES = [
  {
    category: 'Crédito',
    title: 'Cómo construir tu crédito en Canadá desde cero',
    description:
      'Paso a paso para obtener tu primera tarjeta, usar menos del 30% del límite y monitorear tu score gratis.',
    readTime: 10,
    to: '/blog/construir-credito-canada-desde-cero',
  },
  {
    category: 'Ahorro',
    title: 'TFSA vs RRSP vs FHSA: cuál abrir primero como recién llegado',
    description:
      'Diferencias clave, cuál abrir primero según tu ingreso y cómo usar el Home Buyers\' Plan.',
    readTime: 12,
    to: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes',
  },
  {
    category: 'Hipotecas',
    title: 'Cómo elegir tu primera hipoteca en Canadá',
    description:
      'Enganche mínimo, programas para compradores primerizos y qué evalúan los bancos.',
    readTime: 14,
    to: '/blog/primera-hipoteca-canada-guia',
  },
  {
    category: 'Banca',
    title: 'Mejores cuentas bancarias sin fees para recién llegados',
    description:
      'Bancos digitales, paquetes newcomer y cuentas low-cost comparados.',
    readTime: 12,
    to: '/blog/mejores-cuentas-sin-fees-recien-llegados',
  },
];

const BLOG_CATEGORIES = [
  { key: 'todos', label: 'Todos' },
  { key: 'crédito', label: 'Crédito' },
  { key: 'impuestos', label: 'Impuestos' },
  { key: 'hipotecas', label: 'Hipotecas' },
  { key: 'ahorro', label: 'Ahorro' },
  { key: 'banca', label: 'Banca' },
  { key: 'tarjetas de crédito', label: 'Tarjetas de Crédito' },
  { key: 'recién llegados', label: 'Recién Llegados' },
  { key: 'temporada fiscal', label: 'Temporada Fiscal' },
];

const LEARNING_PATHS = [
  {
    title: 'Acabo de llegar a Canadá',
    image: '/images/pathway-arrived.webp',
    color: '#1E3A5F',
    totalTime: '~56 min',
    steps: [
      { postId: 13, title: 'Costo de vida en Montreal 2026', readTime: 12, path: '/blog/costo-de-vida-montreal-2026' },
      { postId: 10, title: 'Cuentas sin fees para recién llegados', readTime: 12, path: '/blog/mejores-cuentas-sin-fees-recien-llegados' },
      { postId: 8, title: 'Construir crédito desde cero', readTime: 10, path: '/blog/construir-credito-canada-desde-cero' },
      { postId: 9, title: 'Tarjetas para newcomers', readTime: 10, path: '/blog/tarjetas-newcomers-que-piden-bancos' },
      { postId: 11, title: 'TFSA vs RRSP vs FHSA', readTime: 12, path: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes' },
    ],
  },
  {
    title: 'Quiero comprar casa',
    image: '/images/pathway-house.webp',
    color: '#F9A825',
    totalTime: '~48 min',
    steps: [
      { postId: 8, title: 'Construir crédito desde cero', readTime: 10, path: '/blog/construir-credito-canada-desde-cero' },
      { postId: 11, title: 'TFSA vs RRSP vs FHSA', readTime: 12, path: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes' },
      { postId: 12, title: 'Primera hipoteca en Canadá', readTime: 14, path: '/blog/primera-hipoteca-canada-guia' },
      { title: 'Calculadora de hipoteca', readTime: 3, path: '/calculadora-hipoteca', isTool: true },
    ],
  },
  {
    title: 'Quiero pagar menos impuestos',
    image: '/images/pathway-taxes.webp',
    color: '#D32F2F',
    totalTime: '~43 min',
    steps: [
      { postId: 6, title: 'T4 explicado', readTime: 11, path: '/blog/t4-explicado-espanol-impuestos-canada' },
      { title: 'RRSP deadline marzo 2026', readTime: 10, path: '/blog/rrsp-deadline-marzo-2026', isDedicated: true },
      { postId: 11, title: 'TFSA vs RRSP vs FHSA', readTime: 12, path: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes' },
      { title: 'CRA vs Revenu Québec', readTime: 10, path: '/blog/cra-vs-revenu-quebec-diferencias', isDedicated: true },
    ],
  },
];

const GUIDE_CLUSTERS = [
  {
    name: 'Crédito en Canadá',
    description: 'Todo lo que necesitas para construir y mantener un buen historial crediticio.',
    color: '#1E3A5F',
    icon: CreditScoreIcon,
    categoryKey: 'crédito',
    articles: [
      { postId: 8 },
      { postId: 9 },
    ],
  },
  {
    name: 'Impuestos en Canadá',
    description: 'Guías para declarar tus impuestos correctamente y aprovechar todos los créditos.',
    color: '#D32F2F',
    icon: ReceiptIcon,
    categoryKey: 'impuestos',
    articles: [
      { postId: 6 },
      { postId: 7 },
    ],
  },
  {
    name: 'Vivienda e Hipotecas',
    description: 'Desde la calculadora hasta la firma: todo sobre comprar casa en Canadá.',
    color: '#F9A825',
    icon: HouseIcon,
    categoryKey: 'hipotecas',
    articles: [
      { postId: 12 },
      { title: 'Calculadora de Hipoteca', readTime: 3, path: '/calculadora-hipoteca', image: '/images/blog/calculadora-hipoteca-canada.jpg', level: 'basico', idealFor: 'Recién llegados, Familias', isTool: true },
    ],
  },
  {
    name: 'Ahorro e Inversión',
    description: 'Aprende a ahorrar inteligentemente y haz crecer tu dinero en Canadá.',
    color: '#2E7D32',
    icon: SavingsIcon,
    categoryKey: 'ahorro',
    articles: [
      { postId: 13 },
      { postId: 11 },
    ],
  },
];

const ACTION_ITEMS = [
  {
    title: 'Comparador de Cuentas',
    description: 'Encuentra la cuenta bancaria ideal sin fees para recién llegados.',
    meta: '2 min',
    to: '/comparar-cuentas',
    icon: AccountBalanceIcon,
    type: 'Comparador',
    primary: true,
  },
  {
    title: 'Calculadora de Hipoteca',
    description: 'Estima tus pagos mensuales y el costo total.',
    meta: '3 min',
    to: '/calculadora-hipoteca',
    icon: CalculateIcon,
    type: 'Herramienta',
  },
  {
    title: 'Planificador de Presupuesto',
    description: 'Organiza ingresos y gastos con el método 50/30/20.',
    meta: '5 min',
    to: '/budget-planner',
    icon: PieChartIcon,
    type: 'Herramienta',
  },
  {
    title: 'Comparador de Tarjetas',
    description: 'Compara tarjetas de crédito lado a lado.',
    meta: '2 min',
    to: '/tarjetas-de-credito',
    icon: CreditCardIcon,
    type: 'Comparador',
  },
];

/* ── Goal selector pills for hero ────────────────────────────────── */

const GOAL_PILLS = [
  { label: 'Recién llegado', color: '#1E3A5F' },
  { label: 'Comprar casa', color: '#F9A825' },
  { label: 'Bajar impuestos', color: '#D32F2F' },
];

/* ── FadeUp ────────────────────────────────────────────────────────── */

const FadeUp = ({ children }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (navigator.webdriver) return;
    const el = ref.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out';
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <Box ref={ref}>{children}</Box>;
};

/* ── LearningPathCard ──────────────────────────────────────────────── */

const LearningPathCard = ({ path }) => {
  const { lng } = useParams();
  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #EEF0F3',
        borderLeft: `4px solid ${path.color}`,
        borderRadius: '16px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,0.07)' },
      }}
    >
      {/* Image */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#F7F9FC', pt: 1.5, pb: 1, px: 2,
        }}
      >
        <Box
          component="img"
          src={path.image}
          alt={path.title}
          loading="lazy"
          sx={{ width: 72, height: 72, objectFit: 'contain' }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ p: { xs: '14px', md: '18px' }, pt: 1.5, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1A1A1A', lineHeight: 1.3 }}>
            {path.title}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 500, flexShrink: 0, ml: 1.5 }}>
            {path.totalTime}
          </Typography>
        </Box>

        {/* Steps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          {path.steps.map((step, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 22, height: 22, borderRadius: '50%',
                  bgcolor: `${path.color}14`, color: path.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Typography
                component={RouterLink}
                to={`/${lng}${step.path}`}
                sx={{
                  fontSize: '13px', fontWeight: 500, color: '#1A1A1A',
                  textDecoration: 'none', flex: 1, lineHeight: 1.3,
                  '&:hover': { color: '#1E3A5F' },
                }}
              >
                {step.title}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#9CA3AF', flexShrink: 0 }}>
                {step.readTime}m
              </Typography>
            </Box>
          ))}
        </Box>

        {/* CTA */}
        <Button
          component={RouterLink}
          to={`/${lng}${path.steps[0].path}`}
          variant="text"
          endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            mt: 2.5, alignSelf: 'flex-start',
            color: path.color, textTransform: 'none',
            fontWeight: 700, fontSize: '13px', px: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Empezar esta ruta
        </Button>
      </Box>
    </Box>
  );
};

/* ── EnhancedArticleCard ───────────────────────────────────────────── */

const EnhancedArticleCard = ({ article }) => {
  const { lng } = useParams();
  const levelCfg = LEVEL_CONFIG[article.level] || LEVEL_CONFIG.basico;
  const linkPath = `/${lng}${article.path || `/blog/${article.slug}`}`;

  return (
    <Card
      sx={{
        borderRadius: '12px',
        border: '1px solid #EEF0F3',
        boxShadow: 'none',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={linkPath}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
      >
        {article.image ? (
          <CardMedia
            component="img"
            height={120}
            image={article.image}
            alt={article.title}
            loading="lazy"
            sx={{ width: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              height: 80, bgcolor: levelCfg.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography sx={{ color: levelCfg.color, fontWeight: 600, fontSize: '12px' }}>
              {article.isTool ? 'Herramienta' : 'Artículo'}
            </Typography>
          </Box>
        )}
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: '14px', '&:last-child': { pb: '14px' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <Chip
              label={levelCfg.label}
              size="small"
              sx={{ bgcolor: levelCfg.bg, color: levelCfg.color, fontWeight: 600, fontSize: '10px', height: 18 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, opacity: 0.5 }}>
              <AccessTime sx={{ fontSize: 10, color: '#9CA3AF' }} />
              <Typography sx={{ fontSize: '10px', color: '#9CA3AF' }}>{article.readTime}m</Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3, mb: 0.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {article.title}
          </Typography>
          {article.excerpt && (
            <Typography
              sx={{
                fontSize: '12px', color: '#5F6B7A', lineHeight: 1.4,
                display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {article.excerpt}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/* ── ClusterSection (capped at 2 cards + "Ver todo") ─────────────── */

const ClusterSection = ({ cluster, onViewAll }) => {
  const IconComp = cluster.icon;

  const resolvedArticles = cluster.articles.map((item) => {
    if (item.postId) return postById(item.postId) || item;
    return item;
  });

  return (
    <Box sx={{ mb: { xs: 5, md: 6 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 36, height: 36, borderRadius: '8px',
            bgcolor: `${cluster.color}14`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <IconComp sx={{ fontSize: 18, color: cluster.color }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1A1A1A', lineHeight: 1.3 }}>
            {cluster.name}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.4 }}>
            {cluster.description}
          </Typography>
        </Box>
        <Button
          onClick={() => onViewAll(cluster.categoryKey)}
          variant="text"
          size="small"
          sx={{
            color: '#1E3A5F', textTransform: 'none', fontWeight: 600,
            fontSize: '12px', flexShrink: 0, display: { xs: 'none', md: 'inline-flex' },
            '&:hover': { bgcolor: 'rgba(30,58,95,0.06)' },
          }}
        >
          Ver todo
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        {resolvedArticles.slice(0, 2).map((article, i) => (
          <Grid item xs={12} sm={6} key={article.id || article.title || i}>
            <EnhancedArticleCard article={article} />
          </Grid>
        ))}
      </Grid>

      {/* Mobile "Ver todo" */}
      <Box sx={{ mt: 2, display: { xs: 'block', md: 'none' } }}>
        <Button
          onClick={() => onViewAll(cluster.categoryKey)}
          variant="text"
          size="small"
          sx={{
            color: cluster.color, textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 0,
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          {`Ver todo sobre ${cluster.name.toLowerCase()} \u2192`}
        </Button>
      </Box>
    </Box>
  );
};

/* ── Page ───────────────────────────────────────────────────────────── */

const BlogPage = () => {
  const { lng } = useParams();
  const { i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('todos');
  const [visiblePosts, setVisiblePosts] = useState(6);
  const postsGridRef = useRef(null);
  const routesRef = useRef(null);

  const handleCategoryChange = (key) => {
    setActiveCategory(key);
    setVisiblePosts(6);
  };

  const handleViewAll = (categoryKey) => {
    setActiveCategory(categoryKey);
    setVisiblePosts(6);
    setTimeout(() => {
      postsGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToRoutes = () => {
    routesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filteredPosts = useMemo(() => {
    if (activeCategory === 'todos') return postsData;
    return postsData.filter((post) =>
      post.tags.some((tag) =>
        tag.toLowerCase().includes(activeCategory.toLowerCase())
      )
    );
  }, [activeCategory]);

  const activeCategoryLabel = BLOG_CATEGORIES.find(
    (c) => c.key === activeCategory
  )?.label;

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Centro de Educación Financiera para Latinos en Canadá',
    description: 'Guías, artículos y herramientas sobre crédito, ahorro, impuestos, hipotecas y más.',
    url: 'https://www.finmaple.com/blog',
    publisher: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    inLanguage: i18n.language,
    availableLanguage: ['es', 'en', 'fr'],
  };

  return (
    <Box sx={{ bgcolor: '#FFFFFF' }}>
      <SEOHead
        seoKey="blog"
        title="Centro de Educación Financiera para Latinos en Canadá"
        description="Guías, artículos y herramientas sobre crédito, ahorro, impuestos, hipotecas y más. Contenido en español escrito por latinos para latinos."
        canonical="/blog"
        schema={blogSchema}
        routeKey="blog"
        noIndex={i18n.language !== 'es'}
      />

      {/* ── 1. SIGNATURE HERO ── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)',
          borderBottom: '1px solid #EEF0F3',
          pt: { xs: 3, md: 3.5 }, pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <BreadcrumbNav items={[{ label: 'Guías' }]} />
          <Typography
            variant="h3" component="h1"
            sx={{
              fontWeight: 700, color: '#0F1E3A', mt: 1.5, mb: 1,
              lineHeight: 1.12, fontSize: { xs: '26px', md: '40px' },
              letterSpacing: '-0.02em', maxWidth: '18ch',
            }}
          >
            Centro de Educación Financiera en Canadá
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '14px', md: '16px' }, color: '#5F6B7A',
              lineHeight: 1.6, maxWidth: '55ch', mb: 2.5,
            }}
          >
            Guías prácticas y herramientas para tomar las mejores decisiones financieras
            desde tu primer día en Canadá.
          </Typography>

          {/* Goal selector pills */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#5F6B7A', alignSelf: 'center', mr: 0.5 }}>
              Empieza con una ruta:
            </Typography>
            {GOAL_PILLS.map((pill) => (
              <Chip
                key={pill.label}
                label={pill.label}
                onClick={scrollToRoutes}
                size="small"
                sx={{
                  fontWeight: 600, fontSize: '13px', height: 32, borderRadius: '10px',
                  bgcolor: '#FFFFFF', color: pill.color, border: `1px solid ${pill.color}30`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: `${pill.color}08`, borderColor: pill.color },
                }}
              />
            ))}
          </Box>

          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 2 }}>
            Actualizado marzo 2026 · Basado en fuentes oficiales · Sin costo para usuarios
          </Typography>
        </Container>
      </Box>

      {/* ── 2. RUTAS RECOMENDADAS ── */}
      <Box
        component="section"
        ref={routesRef}
        sx={{ bgcolor: '#FFFFFF', pt: { xs: 5, md: 6 }, pb: { xs: 4, md: 5 }, scrollMarginTop: '60px' }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}
          >
            Tu camino
          </Typography>
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '20px', md: '26px' }, mb: 0.75 }}
          >
            Rutas recomendadas según tu objetivo
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', mb: 3.5, maxWidth: '55ch' }}>
            Elige una ruta y sigue los pasos en orden para aprender lo esencial.
          </Typography>
          <Grid container spacing={3}>
            {LEARNING_PATHS.map((lp) => (
              <Grid item xs={12} md={4} key={lp.title}>
                <LearningPathCard path={lp} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 3. GUÍAS ESENCIALES (1 featured + 3 secondary) ── */}
      <Box
        component="section"
        sx={{ bgcolor: '#FFFFFF', py: { xs: 4, md: 5 }, borderTop: '1px solid #EEF0F3' }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}
          >
            Empieza aquí
          </Typography>
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '20px', md: '26px' }, mb: 3 }}
          >
            Guías esenciales para recién llegados
          </Typography>

          <Grid container spacing={3}>
            {/* Featured guide (first one) */}
            <Grid item xs={12} md={7}>
              <Box
                component={RouterLink}
                to={`/${lng}${ESSENTIAL_GUIDES[0].to}`}
                sx={{
                  display: 'flex', flexDirection: 'column', height: '100%',
                  bgcolor: '#FFFFFF', border: '1.5px solid #1E3A5F', borderRadius: '16px',
                  p: { xs: 3, md: 4 }, textDecoration: 'none',
                  boxShadow: '0 4px 16px rgba(30,58,95,0.06)',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': { boxShadow: '0 8px 28px rgba(30,58,95,0.10)', transform: 'translateY(-3px)' },
                }}
              >
                <Chip
                  label="Lectura recomendada"
                  size="small"
                  sx={{ alignSelf: 'flex-start', mb: 2, bgcolor: '#1E3A5F', color: '#fff', fontWeight: 700, fontSize: '11px', height: 22 }}
                />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                  {ESSENTIAL_GUIDES[0].category}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '18px', md: '22px' }, color: '#1A1A1A', lineHeight: 1.25, mb: 1.5 }}>
                  {ESSENTIAL_GUIDES[0].title}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.6, flex: 1, mb: 2 }}>
                  {ESSENTIAL_GUIDES[0].description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1E3A5F' }}>
                    Leer guía
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: '#1E3A5F' }} />
                  <Typography sx={{ fontSize: '11px', color: '#9CA3AF', ml: 'auto' }}>
                    {ESSENTIAL_GUIDES[0].readTime} min
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Secondary guides (remaining 3) */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                {ESSENTIAL_GUIDES.slice(1).map((guide) => (
                  <Box
                    key={guide.to}
                    component={RouterLink}
                    to={`/${lng}${guide.to}`}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      bgcolor: '#FFFFFF', border: '1px solid #EEF0F3', borderRadius: '12px',
                      p: 2, textDecoration: 'none', flex: 1,
                      transition: 'all 0.18s ease',
                      '&:hover': { borderColor: '#1E3A5F', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', transform: 'translateY(-2px)' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>
                        {guide.category}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                        {guide.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, opacity: 0.5 }}>
                      <AccessTime sx={{ fontSize: 11, color: '#9CA3AF' }} />
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>{guide.readTime}m</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 4. ARTÍCULOS POR TEMA (capped at 2 per cluster) ── */}
      <Box
        component="section"
        sx={{ bgcolor: '#FFFFFF', borderTop: '1px solid #EEF0F3', pt: { xs: 5, md: 6 }, pb: { xs: 3, md: 4 } }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}
          >
            Explora por tema
          </Typography>
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '20px', md: '26px' }, mb: 4 }}
          >
            Artículos organizados por tema
          </Typography>

          {GUIDE_CLUSTERS.map((cluster) => (
            <FadeUp key={cluster.name}>
              <ClusterSection cluster={cluster} onViewAll={handleViewAll} />
            </FadeUp>
          ))}
        </Container>
      </Box>

      {/* ── 5. EXPLORAR POR CATEGORÍA (filter + grid) ── */}
      <Box
        component="section"
        ref={postsGridRef}
        sx={{
          bgcolor: '#F7F9FC', borderTop: '1px solid #EEF0F3',
          scrollMarginTop: '60px', pt: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '16px', md: '18px' }, mb: 2 }}
          >
            {activeCategory === 'todos'
              ? `Archivo — ${filteredPosts.length} artículos`
              : `${filteredPosts.length} artículo${filteredPosts.length !== 1 ? 's' : ''} sobre ${activeCategoryLabel}`}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 4 }}>
            {BLOG_CATEGORIES.map((cat) => (
              <Chip
                key={cat.key}
                label={cat.label}
                onClick={() => handleCategoryChange(cat.key)}
                size="small"
                sx={{
                  fontWeight: 500, fontSize: '12px', height: 28, borderRadius: '999px',
                  ...(activeCategory === cat.key
                    ? { bgcolor: '#1E3A5F', color: '#FFFFFF' }
                    : { bgcolor: 'transparent', color: '#1A1A1A', border: '1px solid #E6E9EE' }),
                  '&:hover': { opacity: 0.85 },
                }}
              />
            ))}
          </Box>

          {filteredPosts.length === 0 ? (
            <Box sx={{ bgcolor: '#F7F9FC', borderRadius: '14px', py: 6, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '15px', color: '#5F6B7A' }}>
                No hay artículos en esta categoría todavía.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {filteredPosts.slice(0, visiblePosts).map((post, idx) => {
                  const levelCfg = LEVEL_CONFIG[post.level] || LEVEL_CONFIG.basico;
                  return (
                    <Box
                      key={post.id}
                      component={RouterLink}
                      to={`/${lng}${post.path || `/blog/${post.slug}`}`}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        py: 1.5, px: 1,
                        textDecoration: 'none',
                        borderBottom: idx < Math.min(filteredPosts.length, visiblePosts) - 1 ? '1px solid #F0F1F3' : 'none',
                        borderRadius: '8px',
                        transition: 'background 0.15s ease',
                        '&:hover': { bgcolor: '#F7F9FC' },
                      }}
                    >
                      {post.image && (
                        <Box
                          component="img"
                          src={post.image}
                          alt=""
                          loading="lazy"
                          sx={{
                            width: 56, height: 56, borderRadius: '8px',
                            objectFit: 'cover', flexShrink: 0,
                            display: { xs: 'none', sm: 'block' },
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {post.title}
                        </Typography>
                        {post.excerpt && (
                          <Typography sx={{
                            fontSize: '12px', color: '#5F6B7A', lineHeight: 1.4, mt: 0.25,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {post.excerpt}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <Chip
                          label={levelCfg.label}
                          size="small"
                          sx={{
                            bgcolor: levelCfg.bg, color: levelCfg.color,
                            fontWeight: 600, fontSize: '10px', height: 18,
                            display: { xs: 'none', md: 'flex' },
                          }}
                        />
                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {post.readTime}m
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {filteredPosts.length > visiblePosts && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    onClick={() => setVisiblePosts((prev) => prev + 8)}
                    variant="text"
                    size="small"
                    sx={{
                      color: '#1E3A5F', textTransform: 'none',
                      fontWeight: 600, fontSize: '13px',
                      '&:hover': { bgcolor: 'rgba(30,58,95,0.04)' },
                    }}
                  >
                    {`Ver más (${filteredPosts.length - visiblePosts} restantes)`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* ── 6. APLICA LO QUE APRENDISTE (1 primary + 3 secondary) ── */}
      <Box
        component="section"
        sx={{ bgcolor: '#F7F9FC', py: { xs: 5, md: 6 }, borderTop: '1px solid #EEF0F3' }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}
          >
            Siguiente paso
          </Typography>
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '20px', md: '26px' }, mb: 0.5 }}
          >
            Tu mejor siguiente paso
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#5F6B7A', mb: 3 }}>
            Después de leer, ponlo en práctica.
          </Typography>

          {/* Primary action — hero card */}
          {(() => {
            const item = ACTION_ITEMS[0];
            const IconComp = item.icon;
            return (
              <Box
                component={RouterLink}
                to={`/${lng}${item.to}`}
                sx={{
                  display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 },
                  bgcolor: '#FFFFFF', border: '1.5px solid #1E3A5F', borderRadius: '14px',
                  p: { xs: 2.5, md: 3 }, mb: 2, textDecoration: 'none',
                  boxShadow: '0 4px 16px rgba(30,58,95,0.06)',
                  transition: 'all 0.18s ease',
                  '&:hover': { boxShadow: '0 8px 28px rgba(30,58,95,0.10)', transform: 'translateY(-2px)' },
                }}
              >
                <Box
                  sx={{
                    width: 48, height: 48, borderRadius: '12px',
                    bgcolor: '#1E3A5F', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComp sx={{ fontSize: 24, color: '#fff' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: '16px', md: '18px' }, fontWeight: 700, color: '#1A1A1A', lineHeight: 1.3, mb: 0.25 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.5 }}>
                    {item.description}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    height: 38, fontSize: '13px', fontWeight: 700,
                    borderRadius: '10px', bgcolor: '#1E3A5F', color: '#fff', textTransform: 'none',
                    px: 2.5, boxShadow: 'none', flexShrink: 0, whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#1A3152', boxShadow: 'none' },
                  }}
                >
                  Abrir comparador
                </Button>
              </Box>
            );
          })()}

          {/* Secondary actions — compact row */}
          <Grid container spacing={1.5}>
            {ACTION_ITEMS.slice(1).map((item) => {
              const IconComp = item.icon;
              return (
                <Grid item xs={12} sm={4} key={item.title}>
                  <Box
                    component={RouterLink}
                    to={`/${lng}${item.to}`}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      bgcolor: '#FFFFFF', border: '1px solid #EEF0F3', borderRadius: '10px',
                      p: 1.5, textDecoration: 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: '#1E3A5F', bgcolor: '#F7F9FC' },
                    }}
                  >
                    <IconComp sx={{ fontSize: 18, color: '#5F6B7A', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                        {item.meta}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ── 7. METHODOLOGY (inline trust block) ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', py: { xs: 2, md: 2.5 }, borderTop: '1px solid #EEF0F3' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <Typography sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: '13px', mr: 0.5 }}>
              Nuestra metodología:
            </Typography>
            {[
              'Fuentes oficiales canadienses',
              'Actualizado regularmente',
              'Contenido educativo, no asesoría',
            ].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleOutline sx={{ fontSize: 13, color: '#2E7D32' }} />
                <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>{item}</Typography>
              </Box>
            ))}
            <Button
              component={RouterLink}
              to={`/${lng}/como-monetizamos`}
              variant="text"
              size="small"
              sx={{
                textTransform: 'none', fontSize: '12px', fontWeight: 600,
                color: '#1E3A5F', px: 0, ml: { xs: 0, md: 'auto' }, minWidth: 0,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Ver metodología completa
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── 8. TRUST STRIP ── */}
      <TrustStrip />

      {/* ── 9. COMPACT NEWSLETTER ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', borderTop: '1px solid #EEF0F3', py: { xs: 1.5, md: 2 } }}>
        <Container maxWidth="lg">
          <Box
            component="form"
            action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
            method="post"
            onSubmit={() => {
              if (window.plausible) window.plausible('Newsletter Submit', { props: { source: 'blog-compact' } });
              gtmNewsletterSubmit({ source: 'blog-compact' });
            }}
            sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', gap: { xs: 1, sm: 1.5 },
              maxWidth: 520, mx: 'auto',
            }}
          >
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Newsletter semanal:
            </Typography>
            <TextField
              name="email" type="email" required size="small" placeholder="tu@email.com"
              sx={{
                flex: 1, minWidth: 140,
                '& .MuiOutlinedInput-root': {
                  height: 32, borderRadius: '8px', fontSize: '12px',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#1E3A5F' },
                  '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
                },
              }}
            />
            <Button
              type="submit" variant="contained" size="small"
              sx={{
                height: 32, px: 2, fontSize: '12px', fontWeight: 700,
                borderRadius: '8px', bgcolor: '#1E3A5F', color: '#fff',
                textTransform: 'none', whiteSpace: 'nowrap', boxShadow: 'none',
                '&:hover': { bgcolor: '#1A3152', boxShadow: 'none' },
              }}
            >
              Suscribirme
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BlogPage;
